import React, { FC, forwardRef, useMemo } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import DownloadIcon from '../../assets/icons/DownloadIcon';
import LinkedInIcon from '../../assets/icons/LinkedInIcon';
import ShareIcon from '../../assets/icons/ShareIcon';
import Modal, { Modals } from '../../modals/Modal';
import { generateImageKitUrl } from '../../modules/imageKit';
import { colorsPalette } from '../../theme';
import { downloadFile, generateCertificateName, getCertificatePDFUrl } from '../../utils/helpers';
import { useTextStyles } from '../../utils/styles';
import { IKImage } from '../IKImage';
import { IconButton } from '../UI/IconButton';
import { LightTooltip } from '../UI/LightTooltip';
import useStyles from './styles';

interface CertificateListItemProps {
  title?: string | null;
  imageUrl?: string | null;
  slug?: string | null;
  cpdCredits?: string | null;
  hash?: string | null;
  wrapperClass?: string | null;
  tenantTitle?: string | null;
  tenantName?: string | null;
  passDate?: string | null;
  category?: string | null;
}

const CertificateListItem: FC<CertificateListItemProps> = forwardRef(
  (
    {
      title,
      imageUrl,
      slug,
      cpdCredits,
      hash,
      wrapperClass,
      tenantTitle,
      tenantName,
      category,
      passDate,
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const classes = useStyles();
    const textClasses = useTextStyles();
    const { t } = useTranslation();

    const certName = generateCertificateName(tenantTitle, title);
    const certUrl = useMemo(
      () =>
        generateImageKitUrl({
          path: getCertificatePDFUrl(hash, tenantName),
          transformation: undefined,
        }),
      [hash, tenantName],
    );

    return (
      <div ref={ref} className={cn(classes.wrapper, wrapperClass)}>
        <div className={classes.itemLeftBlock}>
          <div className={classes.itemLeftBlockInner}>
            {imageUrl && (
              <div className={classes.previewWrapper}>
                <IKImage
                  path={imageUrl}
                  alt={title}
                  srcset={[{ width: 62, height: 43, crop: 'maintain_ratio' }]}
                  sizes="62px"
                  draggable={false}
                  data-object-fit="cover"
                />
              </div>
            )}
            <div>
              <p className={cn(classes.tableTitle, textClasses.h4)}>
                {slug && (
                  <Link to={`/pathway/${slug}/#certificate`} className={classes.itemHref}>
                    {title}
                  </Link>
                )}
                {!slug && title}
              </p>
              {cpdCredits && (
                <p className={cn(classes.cpdScore, textClasses.meta)}>
                  {cpdCredits} {t('common.cpdCredits')}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className={classes.itemRightBlock}>
          <Modal entityName={title} passDate={passDate} resultHash={hash}>
            {({ openModal }) => (
              <IconButton
                data-testid="openLinkedinModal"
                btnClass={classes.iconBtnWithText}
                icon={<LinkedInIcon color={colorsPalette.primary} className={classes.icon} />}
                label={t('page.pathway.addToProfile')}
                onClick={() => openModal(Modals.LinkedInModal)}
              />
            )}
          </Modal>
          <IconButton
            btnClass={cn(classes.iconBtnWithText, classes.addBtn)}
            icon={<DownloadIcon className={classes.icon} />}
            label={t('page.pathway.cpdCertificate')}
            onClick={() => downloadFile(certName, certUrl)}
          />
          <Modal entityName={title} resultHash={hash} cpdCredits={cpdCredits} category={category}>
            {({ openModal }) => (
              <IconButton
                data-testid="openShareModal"
                btnClass={cn(classes.iconBtnWithText, classes.addBtn)}
                icon={<ShareIcon className={classes.icon} />}
                label={t('page.pathway.share')}
                onClick={() => openModal(Modals.ShareModal)}
              />
            )}
          </Modal>
          <LightTooltip placement="top" title={t('page.pathway.cpdCertificate') || ''}>
            <button className={classes.iconBtn} onClick={() => downloadFile(certName, certUrl)}>
              <DownloadIcon className={classes.icon} />
            </button>
          </LightTooltip>
          <Modal entityName={title} resultHash={hash} cpdCredits={cpdCredits} category={category}>
            {({ openModal }) => (
              <LightTooltip placement="top" title={t('page.pathway.share') || ''}>
                <button className={classes.iconBtn} onClick={() => openModal(Modals.ShareModal)}>
                  <ShareIcon className={classes.icon} />
                </button>
              </LightTooltip>
            )}
          </Modal>
        </div>
      </div>
    );
  },
);

export default CertificateListItem;
